<template>
  <sidebar-layout
    :description="pageData.description"
    :page-title="pageData.pageTitle"
  >
    <div
      v-if="activePageData.warning"
      v-dompurify-html="activePageData.warning"
      class="warning"
    />
    <div
      ref="rewardClassUpgrade"
      class="form-section"
    >
      <div class="info-section">
        <steps
          :active-step="activeStep"
          :total-count="3"
        />
        <div
          v-if="activePageData.title"
          class="info-title"
        >
          {{ activePageData.title }}
        </div>
        <div
          v-if="activePageData.description"
          v-dompurify-html="activePageData.description"
          v-inlineModal
          class="info-description"
        />

        <terms-and-conditions
          v-if="pageData.termsAndConditionContent && (isTablet || isDesktop)"
          :content="pageData.termsAndConditionContent"
          :text="pageData.termsAndConditionLinkText"
          :title="pageData.termsAndConditionTitle"
        />
      </div>
      <div
        :is="`step${activeStep}`"
        class="field-section"
        v-bind="componentProperties"
        @focusForm="focusForm()"
      />
    </div>
  </sidebar-layout>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import FormMixin from 'components/Forms/FormMixin'
  import FormHelpers from 'components/Forms/FormHelpers'
  import isInViewport from 'utils/IsInViewport'
  import Steps from 'components/Forms/Steps'
  import InlineModal from 'utils/directives/InlineModal'

  const Step1 = () =>
    import(
      /* webpackChunkName: "reward-class-upgrade-step1" */ 'containers/Pages/RewardClassUpgrade/FormComponents/PinsSection'
    )
  const Step2 = () =>
    import(
      /* webpackChunkName: "reward-class-upgrade-step2" */ 'containers/Pages/RewardClassUpgrade/FormComponents/PnrValidationForm'
    )
  const Step3 = () =>
    import(
      /* webpackChunkName: "reward-class-upgrade-step3" */ 'containers/Pages/RewardClassUpgrade/FormComponents/PaxFlightSection'
    )

  export default {
    directives: {
      InlineModal
    },

    components: {
      'sidebar-layout': () =>
        import(/* webpackChunkName: "sidebar-layout" */ 'layouts/SidebarLayout'),
      'blue-button': () =>
        import(/* webpackChunkName: "btn-blue" */ 'components/Buttons/Blue'),
      'terms-and-conditions': () =>
        import(
          /* webpackChunkName: "terms-and-conditions" */ 'containers/Pages/RewardClassUpgrade/FormComponents/TermsAndConditions'
        ),
      Step1,
      Step2,
      Step3,
      Steps
    },

    mixins: [FormMixin, FormHelpers],

    data() {
      return {
        activePageData: {}
      }
    },

    computed: {
      ...mapState('rewardClassUpgrade', [
        'pageData',
        'pinsData',
        'formErrors',
        'activeStep'
      ]),
      ...mapGetters('device', ['isMobile', 'isTablet', 'isDesktop']),
      ...mapGetters('rewardClassUpgrade', ['$v']),

      componentProperties: function () {
        return this.stepProps()[`step${this.activeStep}`]
      }
    },

    watch: {
      activeStep: function () {
        this.setActivePageData()
      }
    },

    mounted() {
      this.setActivePageData()
    },

    methods: {
      setActivePageData: function () {
        this.activePageData = this.activePageProps()[`step${this.activeStep}`]
      },

      activePageProps: function () {
        return {
          step1: {
            title: this.pageData.pinsCardTitle,
            description: this.pageData.pinsCardDescription,
            warning: this.pageData.pinsWarning
          },
          step2: {
            title: this.pageData.bookingDataTitle,
            description: this.pageData.bookingDataDescription,
            warning: this.pageData.bookingDataWarning
          },
          step3: {
            title: this.pageData.flightAndPaxTitle,
            description: this.pageData.flightAndPaxDescription,
            warning: this.pageData.flightAndPaxWarning
          }
        }
      },

      stepProps: function () {
        let sharedProps = {
          termsAndConditions: {
            title: this.pageData.termsAndConditionTitle,
            text: this.pageData.termsAndConditionLinkText,
            content: this.pageData.termsAndConditionContent
          }
        }

        return {
          step1: {
            pax: this.pinsData,
            'form-errors': this.formErrors.pinsData,
            sharedProps: sharedProps,
            validation: this.$v
          },
          step2: {
            'form-errors': this.formErrors.pnrData,
            sharedProps: sharedProps,
            validation: this.$v
          },
          step3: {
            'form-errors': this.formErrors.passengers,
            sharedProps: sharedProps
          }
        }
      },

      focusForm() {
        if (!isInViewport(this.$refs.rewardClassUpgrade)) {
          this.$_formHelpers_scrollToError(this.$el, {
            errorElement: '.field-section',
            parents: ['.form-section']
          })
        }
      }
    }
  }
</script>

<style
  lang="scss"
  rel="stylesheet/scss"
  scoped
>
  @import '~assets/styles/common.scss';

  .form-section {
    display: flex;
    flex-direction: column;
    padding: 20px 20px 32px;
    background-color: $blue-bg-css;

    @include bp-mf-tablet {
      justify-content: space-between;
      flex-direction: row;
      padding: 28px 24px 50px;
    }

    ::v-deep .step-container {
      max-width: 120px;
      margin-left: 0;
      padding: 0;

      &:before {
        bottom: 11px;
      }

      .step {
        width: 24px;
        height: 24px;
      }
    }

    .info-section {
      width: 100%;
      position: relative;
      margin-bottom: 20px;

      @include bp-mf-tablet {
        width: calc(39% - 2px);
        margin-bottom: 0;
      }

      @include bp-mf-desktop {
        width: calc(43% + 3px);
        max-width: 240px;

        .action {
          position: absolute;
          bottom: 15px;
        }
      }

      .info-title {
        font-size: em(16);
        line-height: 19px;
        font-weight: 500;
        margin: 20px 0 10px;
      }

      .info-description {
        font-size: em(14);
        line-height: 20px;
        font-weight: 400;
        color: $text-secondary;

        ::v-deep p {
          margin: 0;
        }
      }

      ::v-deep .terms-and-conditions,
      .terms-and-conditions {
        position: absolute;
        bottom: 15px;
      }
    }

    .field-section {
      width: 100%;

      ::v-deep .pnr-form-block {
        padding: 25px 24px 25px 0;
      }

      @include bp-mf-tablet {
        max-width: 390px;
      }
    }
  }

  form {
    background-color: $blue-bg-css;
  }

  .warning {
    padding: 16px 18px;
    margin: 0 20px 23px;
    border-left: 3px solid $orange;
    border-radius: 6px;
    background-color: $warning-bg;
    color: $blue-dark;
    line-height: 20px;

    @include bp-mf-tablet {
      margin: 0 0 23px;
    }

    ::v-deep p {
      margin: 0;
    }
  }

  ::v-deep .field-group {
    input,
    textarea {
      height: 56px !important;
      border: 1px solid #bec3cc !important;
      border-radius: 4px;
      color: $blue-dark !important;

      @include input-placeholder {
        color: $blue-secondary !important;
      }
    }

    &.error input {
      border-color: $red-dark !important;
    }

    .error-msg {
      font-size: em(14);
      line-height: 18px;
      margin-top: 8px;
      color: $red-error;
    }
  }

  .button-wrap {
    padding: 32px 20px;

    @include bp-mf-tablet {
      padding: 40px 0 0;
    }
  }

  .btn-blue {
    @include bp-mf-tablet {
      float: right;
    }
  }
</style>
